import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { header } from '../../components/header.encry';

const API_URL = process.env.REACT_APP_PEGASUS_API_URL || 'https://api.pegasus.le-celadon.ma';
const LABEL_WIDTH_MM = 50.8;
const LABEL_HEIGHT_MM = 127;
const LABEL_DPI = 203;
const LABEL_WIDTH_PX = Math.round((LABEL_WIDTH_MM / 25.4) * LABEL_DPI);
const LABEL_HEIGHT_PX = Math.round((LABEL_HEIGHT_MM / 25.4) * LABEL_DPI);

const createDefaultImageAdjustment = () => ({
  rotation: 0,
  zoom: 1,
  offsetX: 0,
  offsetY: 0,
});

const loadImage = (source) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = reject;
  image.src = source;
});

const getRotatedBounds = (image, rotation) => {
  const normalizedRotation = ((rotation % 360) + 360) % 360;
  const isQuarterTurn = normalizedRotation === 90 || normalizedRotation === 270;
  return {
    width: isQuarterTurn ? image.naturalHeight || image.height : image.naturalWidth || image.width,
    height: isQuarterTurn ? image.naturalWidth || image.width : image.naturalHeight || image.height,
  };
};

const renderAdjustedImageToDataUrl = async (imageSource, adjustment) => {
  const image = await loadImage(imageSource);
  const canvas = document.createElement('canvas');
  canvas.width = LABEL_WIDTH_PX;
  canvas.height = LABEL_HEIGHT_PX;

  const context = canvas.getContext('2d');
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);

  const imageWidth = image.naturalWidth || image.width;
  const imageHeight = image.naturalHeight || image.height;
  const bounds = getRotatedBounds(image, adjustment.rotation);
  const baseScale = Math.min(canvas.width / bounds.width, canvas.height / bounds.height);
  const scale = baseScale * Number(adjustment.zoom || 1);
  const rotationRad = (Number(adjustment.rotation || 0) * Math.PI) / 180;

  context.save();
  context.translate(
    canvas.width / 2 + Number(adjustment.offsetX || 0),
    canvas.height / 2 + Number(adjustment.offsetY || 0)
  );
  context.rotate(rotationRad);
  context.scale(scale, scale);
  context.drawImage(image, -imageWidth / 2, -imageHeight / 2, imageWidth, imageHeight);
  context.restore();

  return canvas.toDataURL('image/jpeg', 0.95);
};

const GlovoMessage = () => {
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('mot');
  const [remotePrinters, setRemotePrinters] = useState([]);
  const [printerUid, setPrinterUid] = useState('');
  const [paperName, setPaperName] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [submittingImage, setSubmittingImage] = useState(false);
  const [loadingPrinters, setLoadingPrinters] = useState(false);
  const [imageAdjustModalOpen, setImageAdjustModalOpen] = useState(false);
  const [imageAdjustment, setImageAdjustment] = useState(createDefaultImageAdjustment());
  const [adjustedImagePreview, setAdjustedImagePreview] = useState('');
  const [imageSize, setImageSize] = useState(null);

  const printerOptions = useMemo(() => (
    (remotePrinters || []).flatMap((remote) => (
      (remote.printers || []).map((printer) => ({
        ...printer,
        computer_name: remote.computer_name || remote.machine_uid || '',
      }))
    ))
  ), [remotePrinters]);

  const selectedPrinter = printerOptions.find((printer) => printer.printer_uid === printerUid);

  const selectedPaper = (selectedPrinter?.paper_sizes || []).find((item) => item.name === paperName);

  const resetImageState = () => {
    setImageFile(null);
    setImageSize(null);
    setImageAdjustModalOpen(false);
    setImageAdjustment(createDefaultImageAdjustment());
    setAdjustedImagePreview('');
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview('');
  };

  const updateImageAdjustment = (field, value) => {
    setImageAdjustment((current) => ({
      ...current,
      [field]: Number(value),
    }));
  };

  const resetImageAdjustment = () => {
    setImageAdjustment(createDefaultImageAdjustment());
  };

  const fillLabelWidth = () => {
    if (!imagePreview || !imageSize) return;
    const bounds = getRotatedBounds(
      { naturalWidth: imageSize.width, naturalHeight: imageSize.height },
      imageAdjustment.rotation
    );
    const containScale = Math.min(LABEL_WIDTH_PX / bounds.width, LABEL_HEIGHT_PX / bounds.height);
    const widthScale = LABEL_WIDTH_PX / bounds.width;
    setImageAdjustment((current) => ({
      ...current,
      zoom: Math.max(0.2, Math.min(4, widthScale / containScale)),
      offsetX: 0,
      offsetY: 0,
    }));
  };

  const rotateImageAdjustment = (delta) => {
    setImageAdjustment((current) => ({
      ...current,
      rotation: (current.rotation + delta + 360) % 360,
      offsetX: 0,
      offsetY: 0,
    }));
  };

  useEffect(() => {
    let ignore = false;

    const fetchPrinters = async () => {
      setLoadingPrinters(true);
      try {
        const response = await axios.get(`${API_URL}/printing/admin/printers`, header());
        if (ignore) return;
        const printers = response.data?.printers || [];
        setRemotePrinters(printers);
        const flattened = printers.flatMap((remote) => (
          (remote.printers || []).map((printer) => ({
            ...printer,
            computer_name: remote.computer_name || remote.machine_uid || '',
          }))
        ));
        const preferred = flattened.find((printer) => printer.is_default) || flattened[0];
        if (preferred?.printer_uid) {
          setPrinterUid((current) => current || preferred.printer_uid);
        }
      } catch (error) {
        console.error('fetch remote printers error:', error);
      } finally {
        if (!ignore) {
          setLoadingPrinters(false);
        }
      }
    };

    fetchPrinters();

    return () => {
      ignore = true;
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, []);

  useEffect(() => {
    if (!imagePreview) {
      setImageSize(null);
      return undefined;
    }

    let ignore = false;
    loadImage(imagePreview)
      .then((image) => {
        if (ignore) return;
        setImageSize({
          width: image.naturalWidth || image.width,
          height: image.naturalHeight || image.height,
        });
      })
      .catch(() => {
        if (!ignore) {
          setImageSize(null);
        }
      });

    return () => {
      ignore = true;
    };
  }, [imagePreview]);

  useEffect(() => {
    if (!imageAdjustModalOpen || !imagePreview) {
      setAdjustedImagePreview('');
      return undefined;
    }

    let ignore = false;
    renderAdjustedImageToDataUrl(imagePreview, imageAdjustment)
      .then((dataUrl) => {
        if (!ignore) {
          setAdjustedImagePreview(dataUrl);
        }
      })
      .catch((error) => {
        console.error('image adjust preview error:', error);
        if (!ignore) {
          setAdjustedImagePreview('');
        }
      });

    return () => {
      ignore = true;
    };
  }, [imageAdjustModalOpen, imagePreview, imageAdjustment]);

  const handleSubmit = () => {
    try {
      sessionStorage.setItem('glovoNoteHtml', message);
      localStorage.setItem('glovoNoteHtml', message);
    } catch (e) {}
    const url = `/glovo-note-print`;
    window.open(url, '_blank', 'width=400,height=500');
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    if (!file) {
      setImageFile(null);
      setImagePreview('');
      return;
    }
    if (file.type && !['image/jpeg', 'image/png'].includes(file.type)) {
      alert('Merci de choisir une image JPG ou PNG.');
      event.target.value = '';
      setImageFile(null);
      setImagePreview('');
      return;
    }
    setImageFile(file);
    setImageSize(null);
    setAdjustedImagePreview('');
    setImageAdjustment(createDefaultImageAdjustment());
    setImagePreview(URL.createObjectURL(file));
  };

  const handleImageSubmit = async () => {
    if (!imageFile || !printerUid) {
      alert('Choisissez une image et une imprimante.');
      return;
    }

    setImageAdjustModalOpen(true);
  };

  const handleAdjustedImageSubmit = async () => {
    if (!imageFile || !printerUid || !imagePreview) {
      alert('Choisissez une image et une imprimante.');
      return;
    }

    setSubmittingImage(true);
    try {
      const imageBase64 = await renderAdjustedImageToDataUrl(imagePreview, imageAdjustment);
      await axios.post(`${API_URL}/printing/admin/queue/image`, {
        printer_uid: printerUid,
        image_base64: imageBase64,
        content_type: 'image/jpeg',
        quantity: 1,
        paper_size: selectedPaper || (paperName ? { name: paperName } : {
          name: '2x5',
          width_mm: LABEL_WIDTH_MM,
          height_mm: LABEL_HEIGHT_MM,
          dpi: LABEL_DPI,
        }),
        title: 'Glovo image',
        metadata: {
          source: 'glovo_message_print_image',
          file_name: imageFile.name,
          created_from_front: true,
          adjusted_from_front: true,
          target_width_mm: LABEL_WIDTH_MM,
          target_height_mm: LABEL_HEIGHT_MM,
          target_width_px: LABEL_WIDTH_PX,
          target_height_px: LABEL_HEIGHT_PX,
          adjustment: imageAdjustment,
        },
      }, header());
      alert('Image ajoutee a la printing queue.');
      resetImageState();
    } catch (error) {
      console.error('print image queue error:', error);
      alert(error?.response?.data?.error || 'Erreur lors de la creation du job image.');
    } finally {
      setSubmittingImage(false);
    }
  };

  return (
    <div className="container mt-4">
      <ul className="nav nav-tabs mb-3" role="tablist">
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === 'mot' ? 'active' : ''}`}
            type="button"
            onClick={() => setActiveTab('mot')}
          >
            <i className="fas fa-comment me-1"></i>
            Mot
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === 'image' ? 'active' : ''}`}
            type="button"
            onClick={() => setActiveTab('image')}
          >
            <i className="fas fa-image me-1"></i>
            Image
          </button>
        </li>
      </ul>

      {activeTab === 'mot' ? (
        <>
          <ReactQuill value={message} onChange={setMessage} />
          <button className="btn btn-primary mt-3" onClick={handleSubmit}>
            Imprimer
          </button>
        </>
      ) : (
        <div className="card shadow-sm border-0">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label fw-bold">Image a imprimer</label>
                <input
                  className="form-control"
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={handleImageChange}
                />
                <small className="text-muted">
                  JPG ou PNG. Avant l'envoi, vous pourrez ajuster l'image au format 50.8 mm x 127 mm.
                </small>
              </div>

              {imagePreview && (
                <div className="col-12">
                  <div className="border rounded p-2 bg-light d-inline-block">
                    <img src={imagePreview} alt="Apercu" style={{ maxWidth: 260, maxHeight: 220, objectFit: 'contain' }} />
                  </div>
                </div>
              )}

              <div className="col-md-7">
                <label className="form-label fw-bold">Imprimante distante</label>
                <select
                  className="form-select"
                  value={printerUid}
                  onChange={(event) => {
                    setPrinterUid(event.target.value);
                    setPaperName('');
                  }}
                  disabled={loadingPrinters}
                >
                  <option value="">{loadingPrinters ? 'Chargement...' : 'Selectionner une imprimante'}</option>
                  {printerOptions.map((printer) => (
                    <option key={printer.printer_uid} value={printer.printer_uid}>
                      {printer.name} - {printer.computer_name || 'Poste'} {printer.is_default ? '(par defaut)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-5">
                <label className="form-label fw-bold">Format papier</label>
                <select
                  className="form-select"
                  value={paperName}
                  onChange={(event) => setPaperName(event.target.value)}
                >
                  <option value="">Automatique</option>
                  {(selectedPrinter?.paper_sizes || []).map((paper) => (
                    <option key={`${paper.name}-${paper.width}-${paper.height}`} value={paper.name}>
                      {paper.name || `${paper.width} x ${paper.height}`}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-12">
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={handleImageSubmit}
                  disabled={!imageFile || !printerUid || submittingImage}
                >
                  {submittingImage ? 'Envoi...' : 'Ajuster et envoyer l image a la queue'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {imageAdjustModalOpen && (
        <div
          className="modal d-block"
          tabIndex="-1"
          role="dialog"
          style={{ background: 'rgba(0,0,0,0.55)' }}
        >
          <div className="modal-dialog modal-xl modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title mb-1">Ajuster l'image avant impression</h5>
                  <div className="text-muted small">
                    Format cible: {LABEL_WIDTH_MM} mm x {LABEL_HEIGHT_MM} mm, {LABEL_DPI} DPI
                    {' '}({LABEL_WIDTH_PX} x {LABEL_HEIGHT_PX} px)
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Fermer"
                  onClick={() => setImageAdjustModalOpen(false)}
                  disabled={submittingImage}
                ></button>
              </div>

              <div className="modal-body">
                <div className="row g-4">
                  <div className="col-lg-5">
                    <div className="d-flex justify-content-center">
                      <div
                        className="bg-white border shadow-sm"
                        style={{
                          width: 203,
                          height: 508,
                          maxHeight: '68vh',
                          aspectRatio: `${LABEL_WIDTH_PX} / ${LABEL_HEIGHT_PX}`,
                          overflow: 'hidden',
                        }}
                      >
                        {adjustedImagePreview ? (
                          <img
                            src={adjustedImagePreview}
                            alt="Apercu ajuste"
                            style={{ width: '100%', height: '100%', display: 'block' }}
                          />
                        ) : (
                          <div className="h-100 d-flex align-items-center justify-content-center text-muted">
                            Preparation...
                          </div>
                        )}
                      </div>
                    </div>
                    {imageSize && (
                      <div className="text-center text-muted small mt-2">
                        Image source: {imageSize.width} x {imageSize.height} px
                      </div>
                    )}
                  </div>

                  <div className="col-lg-7">
                    <div className="card border-0 bg-light">
                      <div className="card-body">
                        <div className="mb-4">
                          <label className="form-label fw-bold">Rotation</label>
                          <div className="d-flex flex-wrap gap-2">
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => rotateImageAdjustment(-90)}
                            >
                              <i className="fas fa-undo me-1"></i>
                              -90
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => rotateImageAdjustment(90)}
                            >
                              <i className="fas fa-redo me-1"></i>
                              +90
                            </button>
                            <span className="badge bg-dark align-self-center">
                              {imageAdjustment.rotation} deg
                            </span>
                          </div>
                        </div>

                        <div className="mb-4">
                          <div className="d-flex justify-content-between">
                            <label className="form-label fw-bold">Taille / zoom</label>
                            <span className="text-muted">{Number(imageAdjustment.zoom || 1).toFixed(2)}x</span>
                          </div>
                          <input
                            className="form-range"
                            type="range"
                            min="0.2"
                            max="4"
                            step="0.01"
                            value={imageAdjustment.zoom}
                            onChange={(event) => updateImageAdjustment('zoom', event.target.value)}
                          />
                          <div className="d-flex flex-wrap gap-2">
                            <button type="button" className="btn btn-sm btn-outline-primary" onClick={resetImageAdjustment}>
                              Ajuster complet
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-primary" onClick={fillLabelWidth}>
                              Remplir la largeur
                            </button>
                          </div>
                        </div>

                        <div className="mb-4">
                          <div className="d-flex justify-content-between">
                            <label className="form-label fw-bold">Deplacement horizontal</label>
                            <span className="text-muted">{imageAdjustment.offsetX}px</span>
                          </div>
                          <input
                            className="form-range"
                            type="range"
                            min="-203"
                            max="203"
                            step="1"
                            value={imageAdjustment.offsetX}
                            onChange={(event) => updateImageAdjustment('offsetX', event.target.value)}
                          />
                        </div>

                        <div className="mb-0">
                          <div className="d-flex justify-content-between">
                            <label className="form-label fw-bold">Deplacement vertical</label>
                            <span className="text-muted">{imageAdjustment.offsetY}px</span>
                          </div>
                          <input
                            className="form-range"
                            type="range"
                            min="-508"
                            max="508"
                            step="1"
                            value={imageAdjustment.offsetY}
                            onChange={(event) => updateImageAdjustment('offsetY', event.target.value)}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="alert alert-warning mt-3 mb-0">
                      Le rendu affiche exactement le ratio du papier. Ce qui est visible ici sera envoye a la queue.
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setImageAdjustModalOpen(false)}
                  disabled={submittingImage}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAdjustedImageSubmit}
                  disabled={submittingImage || !adjustedImagePreview}
                >
                  {submittingImage ? 'Envoi...' : 'Envoyer a la queue'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlovoMessage;
